import React, {
    createContext,
    useEffect,
    useState,
    useContext,
    useMemo,
    useCallback,
} from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import { sepolia } from "wagmi/chains";
import { http, createPublicClient } from "viem";
import { useSmartWallets } from "@privy-io/react-auth/smart-wallets";
import appConfig from "config.json";
import useGetUserData from "Utils/Hooks/Custom/useGetUserData";

export const WalletContext = createContext({});

// eslint-disable-next-line import/no-mutable-exports
export let logoutWallet = null;

export const WalletProvider = ({ children }) => {
    const [walletClient, setWalletClient] = useState();
    const [account, setAccount] = useState();
    const [isConnecting, setIsConnecting] = useState(false);
    const [currentChainId, setCurrentChainId] = useState(null);
    const { ready, authenticated, user, logout, wallet } = usePrivy();
    const { client } = useSmartWallets();
    const { token } = useGetUserData();

    // Define the target chain - Sepolia testnet
    const targetChain = sepolia;

    const publicClient = useMemo(
        () =>
            createPublicClient({
                chain: targetChain,
                transport: http(appConfig.config.alchemy_rpc_url),
            }),
        []
    );

    // Original connectWallet function for smart wallet
    const connectWallet = useCallback(async () => {
        if (!token) return;

        try {
            setIsConnecting(true);
            if (!user?.linkedAccounts) {
                throw new Error("No user accounts available");
            }
            const smartWallet = user.linkedAccounts.find(
                (userAccount) => userAccount.type === "smart_wallet"
            );
            if (!smartWallet?.address) {
                throw new Error("No smart wallet found");
            }
            setWalletClient(client);
            setAccount(smartWallet.address);
            console.log("Smart wallet address", smartWallet.address);
        } catch (err) {
            console.error("Error connecting wallet:", err);
            throw err;
        } finally {
            setIsConnecting(false);
        }
    }, [user, client, token]);

    const disconnect = useCallback(async () => {
        setAccount(undefined);
        setWalletClient(undefined);
        await logout();
    }, [logout]);

    // const postWalletAddress = async () => {
    //     if (account && token) {
    //         await API({
    //             endpoint: "bank/assign-wallet-address",
    //             method: "POST",
    //             params: { walletAddress: account },
    //         });
    //     }
    // };

    useEffect(() => {
        logoutWallet = disconnect;
    }, [disconnect]);

    // Connect wallet as soon as token and other requirements are available
    useEffect(() => {
        if (token && !account && authenticated && ready && !isConnecting) {
            connectWallet();
        }
    }, [token, account, authenticated, ready, isConnecting, connectWallet]);

    // Add chain info to the debug output
    
    const contextValue = useMemo(
        () => ({
            ready,
            authenticated,
            user,
            logout,
            walletClient,
            disconnect,
            connectWallet,
            publicClient,
            isConnecting,
            account,
            token,
            currentChainId,
            targetChainId: targetChain.id,
            isCorrectChain: currentChainId === targetChain.id,
            wallet,
            client,
            smartWalletAddress: client?.address,
            isSmartWallet: account === client?.address,
        }),
        [
            ready,
            authenticated,
            user,
            logout,
            walletClient,
            disconnect,
            connectWallet,
            publicClient,
            isConnecting,
            account,
            token,
            wallet,
            client,
        ]
    );

    return (
        <WalletContext.Provider value={contextValue}>
            {children}
        </WalletContext.Provider>
    );
};

export const useWallet = () => {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error("useWallet must be used within a WalletProvider");
    }
    return context;
};

export default WalletProvider;

